import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import Checkbox from '@/components/Forms/Checkbox';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { FreelancerRoleResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormData = {
  name: string;
  hide_from_dropdowns: boolean;
};

interface Props {
  role?: FreelancerRoleResource;
  onClose: () => void;
}

const FreelancerRoleFormModal: FC<Props> = ({ role, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: role?.name || '',
      hide_from_dropdowns: role?.hide_from_dropdowns || false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (formData) => {
      try {
        const data = {
          name: formData.name,
          hide_from_dropdowns: formData.hide_from_dropdowns,
        };

        if (role) {
          await api.put<typeof data, FreelancerRoleResource>(
            `/v1/freelancer-roles/${role.id}`,
            data,
          );
        } else {
          await api.post<typeof data, FreelancerRoleResource>(
            '/v1/freelancer-roles',
            data,
          );
        }

        await queryClient.invalidateQueries(['freelancer_roles'], {
          exact: false,
        });

        onClose();
        alerts.success(
          `Role was successfully ${role ? 'updated' : 'created'}`,
        );
      } catch (error) {
        handleFormErrors<FormData>(error, setError, alerts);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${role ? 'Edit' : 'Add'} Role`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormData>
                control={control}
                name="name"
                label="Name"
                id="nameInput"
                placeholder="Add role name"
              />
              <Larger>
                <Checkbox control={control} name="hide_from_dropdowns">
                  Hide from dropdowns
                </Checkbox>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {role ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerRoleFormModal;
